'use client';

import { Endpoints } from '@/lib/Endpoints';
import { Requester } from '@/lib/Requester';
import { isRatingFlowSessionLocked, RATING_FLOW_SESSION_LOCK_EVENT } from '@/lib/PopupSessionLocks';
import { GenericResponse } from '@/models/GenericResponse';
import { PromoGame } from '@/models/PromoGame';
import { useToast } from '@/components/ui/use-toast';
import dynamic from 'next/dynamic';
import {
  applyNativeAttributionPromoCode,
  CLAW_PROMO_CODE_SESSION_KEY,
  NATIVE_PROMO_CODE_EVENT,
} from '@/lib/nativeAttributionPromoCode';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { safeLocalStorage, safeSessionStorage } from '@/lib/safeStorage';
import { isAppClient } from '@/lib/LocalStorage';

const ClawMachinePhysics = dynamic(() => import('@/components/claw/ClawMachinePhysics'), {
  ssr: false,
});

const CLAW_POPUP_DELAY_MS = 15_000;
const CLAW_POPUP_STORAGE_KEY = 'claw_last_shown_date';
const CLAW_PROMO_CODE_CLEAR_EVENT = 'claw_clear_code';
const CLAW_GAME_TYPE = 10;
const NATIVE_INFO_POPUP_SESSION_KEY = 'native_attribution_info_popup';
const NATIVE_INFO_POPUP_EVENT = 'hzc_native_info_popup';

function getLocalDateKey(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export default function ClawDailyPopup({ appChipOnly = false }: { appChipOnly?: boolean }) {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const [chipOnly, setChipOnly] = useState(appChipOnly);
  const [promoGame, setPromoGame] = useState<PromoGame | null>(null);
  const [fixedPromoCode, setFixedPromoCode] = useState<string | null>(null);
  const { toast } = useToast();
  /** SSR ile istemci TZ farkından kaçınmak için gün anahtarı yalnızca mount sonrası */
  const [todayKey, setTodayKey] = useState<string | null>(null);

  useEffect(() => {
    setChipOnly(isAppClient() || appChipOnly);
  }, [appChipOnly]);

  useEffect(() => {
    setTodayKey(getLocalDateKey());
  }, []);

  async function copyPromoCodeToClipboard() {
    if (!fixedPromoCode) return;

    try {
      await navigator.clipboard.writeText(fixedPromoCode);
      toast({
        description: 'Hediye kodu kopyalandı',
      });
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = fixedPromoCode;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      textArea.remove();
      toast({
        description: 'Hediye kodu kopyalandı',
      });
    }
  }

  useEffect(() => {
    const code = safeSessionStorage.getItem(CLAW_PROMO_CODE_SESSION_KEY);
    if (code) setFixedPromoCode(code);

    const clearPromoCode = () => {
      setFixedPromoCode(null);
      safeSessionStorage.removeItem(CLAW_PROMO_CODE_SESSION_KEY);
    };

    const onNativePromo = (ev: Event) => {
      const detail = (ev as CustomEvent<{ code?: string; promoId?: number | null }>).detail;
      if (detail?.code) {
        setFixedPromoCode(detail.code);
      }
    };

    (
      window as unknown as {
        __hzcSetNativePromoCode?: (c: string) => void;
        __hzcSetNativeAttributionInfoPopup?: (t: string) => void;
      }
    ).__hzcSetNativePromoCode = (code: string) => {
      applyNativeAttributionPromoCode(code);
    };

    (
      window as unknown as {
        __hzcSetNativePromoCode?: (c: string) => void;
        __hzcSetNativeAttributionInfoPopup?: (t: string) => void;
      }
    ).__hzcSetNativeAttributionInfoPopup = (text: string) => {
      const t = String(text || '').trim();
      if (!t) return;
      safeSessionStorage.setItem(NATIVE_INFO_POPUP_SESSION_KEY, t);
      try {
        window.dispatchEvent(new CustomEvent(NATIVE_INFO_POPUP_EVENT, { detail: { text: t } }));
      } catch {}
    };

    window.addEventListener(CLAW_PROMO_CODE_CLEAR_EVENT, clearPromoCode);
    window.addEventListener(NATIVE_PROMO_CODE_EVENT, onNativePromo);

    return () => {
      delete (
        window as unknown as {
          __hzcSetNativePromoCode?: (c: string) => void;
          __hzcSetNativeAttributionInfoPopup?: (t: string) => void;
        }
      ).__hzcSetNativePromoCode;
      delete (
        window as unknown as {
          __hzcSetNativePromoCode?: (c: string) => void;
          __hzcSetNativeAttributionInfoPopup?: (t: string) => void;
        }
      ).__hzcSetNativeAttributionInfoPopup;
      window.removeEventListener(CLAW_PROMO_CODE_CLEAR_EVENT, clearPromoCode);
      window.removeEventListener(NATIVE_PROMO_CODE_EVENT, onNativePromo);
    };
  }, []);

  useEffect(() => {
    if (chipOnly || todayKey === null) {
      return;
    }

    let timer: ReturnType<typeof setTimeout> | null = null;

    if (isRatingFlowSessionLocked()) {
      return;
    }

    const lastShownDate = safeLocalStorage.getItem(CLAW_POPUP_STORAGE_KEY);
    if (lastShownDate === todayKey) return;

    timer = setTimeout(async () => {
      if (isRatingFlowSessionLocked()) {
        return;
      }

      try {
        const { data: result } = await Requester.get<GenericResponse<PromoGame | null>>(
          Endpoints.GET_PROMO_GAME,
        );
        const game = result?.data;
        if (
          !game ||
          game.gameType !== CLAW_GAME_TYPE ||
          !Array.isArray(game.promoList) ||
          game.promoList.length === 0
        ) {
          return;
        }

        setPromoGame(game);

        safeLocalStorage.setItem(CLAW_POPUP_STORAGE_KEY, todayKey);

        setIsOpen(true);
      } catch {
        // geçerli oyun bilgisi alınamazsa popup gösterme
      }
    }, CLAW_POPUP_DELAY_MS);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [todayKey, chipOnly]);

  useEffect(() => {
    const onRatingLock = () => {
      setIsOpen(false);
    };

    window.addEventListener(RATING_FLOW_SESSION_LOCK_EVENT, onRatingLock);
    return () => {
      window.removeEventListener(RATING_FLOW_SESSION_LOCK_EVENT, onRatingLock);
    };
  }, []);

  return (
    <>
      {!chipOnly && (
        <ClawMachinePhysics
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          gameId={promoGame?.id}
          theme={Number(promoGame?.theme) === 20 ? 20 : 10}
          gameTitle={promoGame?.title}
          popupId={promoGame?.popupId}
          campaignType={promoGame?.campaignType}
          promoList={promoGame?.promoList || []}
          onPrizeWon={(prize) => {
            if (prize.pickedCode) {
              applyNativeAttributionPromoCode(prize.pickedCode);
            }
          }}
        />
      )}

      {fixedPromoCode && (!chipOnly || pathname === '/') && (
        <button
          type="button"
          onClick={copyPromoCodeToClipboard}
          className="fixed left-3 bottom-4 sm:left-4 sm:bottom-6 z-[70] rounded-xl border border-white/20 bg-[#2B3280] px-3 py-2 text-left text-white shadow-lg"
          aria-label="Hediye kodunu kopyala"
        >
          <div className="text-[11px] text-white/70 leading-none">Hediye Kodu</div>
          <div className="text-sm font-semibold tracking-wide">{fixedPromoCode}</div>
        </button>
      )}
    </>
  );
}
